import { Button, mergeClasses, Tooltip } from "@fluentui/react-components";
import {
  ArrowClockwiseRegular,
  ArrowRightRegular,
} from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { ScoreProjectDialog } from "apps/decooda/components/projects/ScoreProjectDialog";
import { IProject } from "apps/decooda/components/projects/projectCard.types";
import {
  ProjectStatus,
  getStatusDisplayText,
} from "apps/decooda/components/projects/projectConstants";
import { useSharedButtonStyles } from "theme/sharedStyles";
import { useAnalyticsRunStyles } from "./AnalyticsRun.styles";
import { IAnalyticsRunProps, IProjectWithProgress } from "./AnalyticsRun.types";

const SCORING_STATUS_DISPLAY: Record<string, string> = {
  Scored: "SCORED",
  Error: "FAILED",
  "In Progress": "IN PROGRESS",
};

const formatDuration = (startedAt: string, completedAt?: string): string => {
  if (!completedAt) return "In progress";
  const seconds = Math.round(
    (new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000,
  );
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) return `${remainingSeconds}s`;
  return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
};

export const AnalyticsRun = ({
  project,
  isPendingScore,
  onViewResults,
  onProjectUpdated,
  onScoringInitiated,
}: IAnalyticsRunProps): JSX.Element => {
  const styles = useAnalyticsRunStyles();
  const sharedButtonStyles = useSharedButtonStyles();
  const [isScoreDialogOpen, setIsScoreDialogOpen] = useState(false);

  const handleScoringStarted = () => {
    onScoringInitiated();
  };

  const projectWithProgress = project as IProject & IProjectWithProgress;
  const scoringProgress = projectWithProgress.scoringProgress ?? 0;
  const scoringHistory = projectWithProgress.scoringHistory ?? [];

  const sortedHistory = useMemo(
    () => [...scoringHistory].sort((a, b) => b.runNumber - a.runNumber),
    [scoringHistory],
  );

  const isInProgress =
    isPendingScore || project.status === ProjectStatus.InProgress;
  const isScored = project.status === ProjectStatus.Scored;
  const isError = project.status === ProjectStatus.Error;

  const attachments = project.attachments ?? [];
  const isAttachmentsConfigured = useMemo(
    () => attachments.length > 0 && attachments.some((a) => !!a.contentColumn),
    [attachments],
  );

  const statusBadgeClass = useMemo(() => {
    if (isScored)
      return mergeClasses(styles.statusBadge, styles.statusBadgeScored);
    if (isInProgress)
      return mergeClasses(styles.statusBadge, styles.statusBadgeInProgress);
    if (isError)
      return mergeClasses(styles.statusBadge, styles.statusBadgeError);
    return undefined;
  }, [isScored, isInProgress, isError, styles]);

  const footerStatusText = useMemo(() => {
    if (isScored)
      return "Scoring completed successfully — results are ready to review.";
    if (isInProgress)
      return "Scoring in progress — this may take several minutes.";
    if (isError) return "Scoring failed. Check your attachments and try again.";
    if (!isAttachmentsConfigured)
      return "Configure at least one attachment with a content column before scoring.";
    return "Ready to score. Click Score to begin.";
  }, [
    isScored,
    isInProgress,
    isError,
    isAttachmentsConfigured,
    project.scoringMessage,
  ]);

  const progressValue = isScored ? 100 : scoringProgress;

  return (
    <div className={styles.root}>
      {/* Run Panel */}
      <div className={styles.panel}>
        <div className={styles.panelHeaderRow}>
          <h2 className={styles.panelTitle}>Run</h2>
          {statusBadgeClass && (
            <span
              className={statusBadgeClass}
              aria-label={`Scoring status: ${getStatusDisplayText(project.status)}`}
            >
              {isScored && "✓ "}
              {getStatusDisplayText(project.status)}
            </span>
          )}
        </div>

        <div className={styles.stepList}>
          <div className={styles.stepRow}>
            <span className={styles.stepBadge} aria-hidden="true">
              1
            </span>
            <p className={styles.stepContent}>
              <strong>Attachments configured</strong>{" "}
              {isAttachmentsConfigured
                ? `${attachments.length} attachment${attachments.length === 1 ? "" : "s"} uploaded, each with a content column selected.`
                : "Add at least one attachment with a content column to enable scoring."}
            </p>
            {isAttachmentsConfigured && (
              <span className={styles.stepPill} aria-label="Step ready">
                ✓ READY
              </span>
            )}
          </div>

          <div className={styles.stepRow}>
            <span className={styles.stepBadge} aria-hidden="true">
              2
            </span>
            <p className={styles.stepContent}>
              <strong>Score the project</strong> Runs in the background and may
              take several minutes depending on your data volume.
            </p>
            {isScored && (
              <span className={styles.stepPill} aria-label="Step complete">
                ✓ COMPLETE
              </span>
            )}
          </div>
        </div>

        {(isInProgress || isScored) && (
          <div className={styles.progressSection} aria-live="polite">
            <div
              className={styles.progressBar}
              role="progressbar"
              aria-valuenow={progressValue}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={styles.progressFill}
                style={{ width: `${progressValue}%` }}
              />
            </div>
            <div className={styles.progressMeta}>
              <span>
                {isScored
                  ? "Scoring completed"
                  : isPendingScore
                    ? "Starting…"
                    : (project.scoringMessage ?? "Processing…")}
              </span>
              <span>{progressValue}% complete</span>
            </div>
          </div>
        )}

        <hr className={styles.divider} />

        <div className={styles.footer}>
          <p className={styles.footerText}>{footerStatusText}</p>
          <div className={styles.footerActions}>
            {!isScored && !isInProgress && (
              <Tooltip
                content={
                  !isAttachmentsConfigured
                    ? "Configure attachments before scoring"
                    : "Score the project"
                }
                relationship="label"
              >
                <Button
                  appearance="primary"
                  className={sharedButtonStyles.primaryButton}
                  onClick={() => setIsScoreDialogOpen(true)}
                  disabled={!isAttachmentsConfigured}
                >
                  Score
                </Button>
              </Tooltip>
            )}
            {(isScored || isError) && (
              <Button
                appearance="outline"
                icon={<ArrowClockwiseRegular aria-hidden="true" />}
                onClick={() => setIsScoreDialogOpen(true)}
                disabled={isInProgress}
              >
                Re-score
              </Button>
            )}
            {isScored && (
              <Button
                appearance="primary"
                className={sharedButtonStyles.primaryButton}
                icon={<ArrowRightRegular aria-hidden="true" />}
                iconPosition="after"
                onClick={onViewResults}
              >
                View results
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.panel}>
        <h2 className={styles.panelTitle}>Scoring history</h2>
        <hr className={styles.divider} />
        <table className={styles.historyTable} aria-label="Scoring history">
          <thead className={styles.historyThead}>
            <tr>
              <th className={styles.historyTh} scope="col">
                Run
              </th>
              <th className={styles.historyTh} scope="col">
                Started
              </th>
              <th className={styles.historyTh} scope="col">
                Duration
              </th>
              <th className={styles.historyTh} scope="col">
                Attachments
              </th>
              <th className={styles.historyTh} scope="col">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedHistory.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.historyEmpty}>
                  No scoring runs yet.
                </td>
              </tr>
            ) : (
              sortedHistory.map((entry) => {
                const startedDate = new Date(
                  entry.startedAt,
                ).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                });
                const startedTime = new Date(
                  entry.startedAt,
                ).toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                });
                const pillClass =
                  entry.status === "Scored"
                    ? mergeClasses(styles.statusPill, styles.statusPillScored)
                    : entry.status === "Error"
                      ? mergeClasses(styles.statusPill, styles.statusPillError)
                      : mergeClasses(
                          styles.statusPill,
                          styles.statusPillInProgress,
                        );
                return (
                  <tr key={entry.runNumber}>
                    <td className={styles.historyTd}>Run {entry.runNumber}</td>
                    <td className={styles.historyTd}>
                      {startedDate}, {startedTime}
                    </td>
                    <td className={styles.historyTd}>
                      {formatDuration(entry.startedAt, entry.completedAt)}
                    </td>
                    <td className={styles.historyTd}>
                      {entry.attachmentsProcessed} of {entry.attachmentsTotal}
                    </td>
                    <td className={styles.historyTd}>
                      <span className={pillClass}>
                        {SCORING_STATUS_DISPLAY[entry.status] ??
                          entry.status.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ScoreProjectDialog
        isOpen={isScoreDialogOpen}
        project={project}
        onDismiss={() => setIsScoreDialogOpen(false)}
        onScoringStarted={handleScoringStarted}
        onScoringComplete={onProjectUpdated}
      />
    </div>
  );
};
